import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {Chip} from 'primeng/chip';
import {InputText} from 'primeng/inputtext';
import {MultiSelect} from 'primeng/multiselect';
import {SelectButton} from 'primeng/selectbutton';
import {TableModule} from 'primeng/table';
import {Tooltip} from 'primeng/tooltip';
import {AbrirSei} from '@components/commons/abrir-sei/abrir-sei';
import {ProcessoSei} from '@core/models/processo-sei';
import {BotSeiService, LinhaBot, ROTULO_SEI, ROTULO_SITUACAO, Situacao} from '@core/services/bot-sei.service';
import {SeiService} from '@core/services/sei.service';
import {AuthService} from '@core/services/auth.service';

/**
 * Tela do Bot SisNot ↔ SEI. A pesquisa roda no BotSeiService: dá pra sair
 * daqui, ir para outras telas e voltar — o bot continua pesquisando.
 */
@Component({
  selector: 'app-bot-sei',
  imports: [FormsModule, DatePipe, RouterLink, Button, Card, Chip, InputText, MultiSelect, SelectButton, TableModule, Tooltip, AbrirSei],
  templateUrl: './bot-sei.html',
})
export class BotSei implements OnInit {
  protected readonly rotuloSei = ROTULO_SEI;
  protected readonly rotuloSituacao = ROTULO_SITUACAO;

  protected sei = inject(SeiService);
  protected bot = inject(BotSeiService);
  /** Só quem pode editar notificação grava a Localização. */
  protected podeEditar = inject(AuthService).hasPermission('editar_notificacao');

  /** Caixas (unidades exatas) do SEI: digita e Enter (ou vírgula) pra cada uma. */
  protected caixas = signal<string[]>([...this.bot.estado().caixas]);
  protected novaCaixa = '';
  // filtros de quais notificações entram na conferência
  protected status = signal<string[]>([]);
  protected superintendencias = signal<string[]>([]);
  protected concessionarias = signal<string[]>([]);
  protected verSituacao = signal<Situacao | 'todas' | 'so-sei'>('todas');

  protected opcoes = computed(() => {
    const distintos = (f: (p: ProcessoSei) => string | null) =>
      [...new Set(this.bot.processos().map(f).filter((v): v is string => !!v))].sort().map(v => ({label: v, value: v}));
    return {
      status: distintos(p => p.status),
      superintendencias: distintos(p => p.superintendencia),
      concessionarias: distintos(p => p.concessionaria),
    };
  });

  /** Notificações que passam nos filtros (antes de iniciar). */
  protected selecionados = computed(() => {
    const st = this.status(), sup = this.superintendencias(), con = this.concessionarias();
    return this.bot.processos().filter(p =>
      (!st.length || st.includes(p.status ?? '')) &&
      (!sup.length || sup.includes(p.superintendencia ?? '')) &&
      (!con.length || con.includes(p.concessionaria ?? '')));
  });
  protected numerosSelecionados = computed(() => [...new Set(this.selecionados().map(p => this.bot.numero(p)))]);

  // filtros das colunas do relatório (texto: contém; listas: qualquer uma das marcadas)
  protected fNot = signal('');
  protected fProcesso = signal('');
  protected fConcessionaria = signal<string[]>([]);
  protected fStatus = signal<string[]>([]);
  protected fSituacao = signal<string[]>([]);
  protected fUnidade = signal<string[]>([]);
  protected fResultado = signal<string[]>([]);
  protected temFiltro = computed(() => !!(this.fNot() || this.fProcesso() || this.fConcessionaria().length || this.fStatus().length
    || this.fSituacao().length || this.fUnidade().length || this.fResultado().length));

  /** Opções dos filtros de lista, tiradas do próprio relatório. */
  protected opcoesColunas = computed(() => {
    const lista = (valores: (string | null | undefined)[]) =>
      [...new Set(valores.filter((v): v is string => !!v))].sort().map(v => ({label: v, value: v}));
    const linhas = this.bot.linhas();
    return {
      concessionaria: lista(linhas.map(l => l.n.concessionaria)),
      status: lista(linhas.map(l => l.n.status)),
      situacao: lista(linhas.map(l => this.situacaoSei(l))),
      unidade: lista(linhas.flatMap(l => (l.r?.unidades ?? []).map(u => this.bot.sigla(u) || u))),
      resultado: lista(linhas.map(l => ROTULO_SITUACAO[l.situacao])),
    };
  });

  protected linhasVisiveis = computed(() => {
    const ver = this.verSituacao();
    const sem = (t: string | null | undefined) => (t ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const not = sem(this.fNot()).trim(), proc = this.fProcesso().replace(/\s/g, '');
    const con = this.fConcessionaria(), st = this.fStatus(), sit = this.fSituacao(), uni = this.fUnidade(), res = this.fResultado();
    return this.bot.linhas().filter(l =>
      (ver === 'todas' || ver === 'so-sei' || l.situacao === ver) &&
      (!not || sem(l.n.numeroNotificacao).includes(not)) &&
      (!proc || l.numero.includes(proc) || l.n.processo.includes(proc)) &&
      (!con.length || con.includes(l.n.concessionaria ?? '')) &&
      (!st.length || st.includes(l.n.status ?? '')) &&
      (!sit.length || sit.includes(this.situacaoSei(l))) &&
      (!uni.length || (l.r?.unidades ?? []).some(u => uni.includes(this.bot.sigla(u) || u))) &&
      (!res.length || res.includes(ROTULO_SITUACAO[l.situacao])));
  });

  /** Texto da coluna "Situação no SEI" (também usado no filtro). */
  protected situacaoSei(l: LinhaBot): string {
    return l.r ? (ROTULO_SEI[l.r.status] ?? l.r.status) : 'Na fila';
  }

  protected readonly opcoesModo = [
    {label: 'Apenas consultar', value: false},
    {label: 'Consultar e atualizar a Localização', value: true},
  ];

  protected gravarLocalizacoes() {
    const n = this.bot.localizacoesPendentes().length;
    if (!confirm(`Gravar a Localização de ${n} notificação(ões) com a(s) unidade(s) do SEI? Cada mudança fica no Histórico.`)) return;
    this.bot.gravarLocalizacoes();
  }

  protected limparFiltros() {
    this.fNot.set('');
    this.fProcesso.set('');
    [this.fConcessionaria, this.fStatus, this.fSituacao, this.fUnidade, this.fResultado].forEach(f => f.set([]));
  }

  protected baixar() {
    // com filtro (ou numa aba): baixa só o que está na tela
    const filtrado = this.temFiltro() || (this.verSituacao() !== 'todas' && this.verSituacao() !== 'so-sei');
    this.bot.baixar(filtrado ? this.linhasVisiveis() : undefined);
  }

  protected opcoesVer = computed(() => {
    const c = this.bot.contagem();
    const lista: {label: string; value: string}[] = [
      {label: `Todas (${this.bot.linhas().length})`, value: 'todas'},
      {label: `Na caixa (${c['na-caixa']})`, value: 'na-caixa'},
      {label: `Em outra unidade (${c['outra-unidade']})`, value: 'outra-unidade'},
      {label: `Concluído (${c.concluido})`, value: 'concluido'},
      {label: `Não encontrado (${c['nao-encontrado']})`, value: 'nao-encontrado'},
      {label: `Outros (${c.outro})`, value: 'outro'},
      {label: `Na fila (${c.pendente})`, value: 'pendente'},
    ];
    if (this.bot.algumaCaixaLida() || this.bot.lendoCaixa()) lista.push({label: `Só no SEI (${this.bot.soNoSei().length})`, value: 'so-sei'});
    return lista;
  });

  ngOnInit() {
    this.bot.carregar();
  }

  /** Põe na lista o que foi digitado (aceita várias separadas por vírgula, ; ou espaço). */
  adicionarCaixa(ev?: Event) {
    ev?.preventDefault();
    const novas = this.novaCaixa.toUpperCase().split(/[,;\s]+/).map(c => c.trim()).filter(c => /^[A-Z0-9_]+(-[A-Z0-9_]+)*$/.test(c));
    if (novas.length) this.caixas.update(l => [...new Set([...l, ...novas])]);
    this.novaCaixa = '';
  }

  removerCaixa(caixa: string) {
    this.caixas.update(l => l.filter(c => c !== caixa));
  }

  iniciar() {
    this.adicionarCaixa();
    if (!this.caixas().length) { alert('Informe a caixa (unidade) do SEI. Ex.: ARTESP-SUROD-SUP'); return; }
    const fila = this.numerosSelecionados();
    if (!fila.length) { alert('Nenhuma notificação com processo SEI nos filtros escolhidos.'); return; }
    if (this.bot.progresso().total && !confirm('Começar uma conferência nova? A anterior será descartada.')) return;
    this.verSituacao.set('todas');
    this.bot.iniciar(this.caixas(), fila, this.podeEditar && this.bot.estado().atualizarLocalizacao);
  }

  aplicarCaixas() {
    this.adicionarCaixa();
    if (!this.caixas().length) return;
    this.bot.aplicarCaixas(this.caixas());
  }

  limpar() {
    if (!confirm('Limpar a conferência e os resultados?')) return;
    this.bot.limpar();
    this.verSituacao.set('todas');
  }

  protected corSituacao(s: Situacao): string {
    return {
      'na-caixa': 'text-green-700 dark:text-green-400', 'outra-unidade': 'text-amber-700 dark:text-amber-400',
      concluido: 'text-indigo-700 dark:text-indigo-300', 'nao-encontrado': 'text-red-700 dark:text-red-400',
      outro: 'text-gray-600 dark:text-gray-300', pendente: 'text-gray-400',
    }[s];
  }
}
